import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router';
import FieldRow from '../components/onboarding/FieldRow';
import ProgressRail from '../components/onboarding/ProgressRail';
import RoleCard from '../components/onboarding/RoleCard';
import StepBar from '../components/StepBar';
import UnlockToast from '../components/onboarding/UnlockToast';
import { ROLES, USER_TYPES, type UserType } from '../lib/onboarding/roles';
import {
  badgesFor,
  completion,
  earnedXp,
  isAnswered,
  maxXp,
  rankOf,
  stepComplete,
  type Answers
} from '../lib/onboarding/progress';
import { saveOnboarding, type OnboardingProfile } from '../lib/onboarding/store';
import { stepsFor, type Badge } from '../lib/onboarding/steps';
import type { OnboardingLoaderData } from '../lib/types';

type Mode = 'induct' | 'edit';
type Phase = 'role' | 'steps';

interface InductionProps extends OnboardingLoaderData {
  mode: Mode;
}

/**
 * The induction.
 *
 * Progressive by design — four doors, then two to five short panels, never the
 * whole registration form at once. The game layer on top is deliberately thin:
 * a price on every question, a rank that moves, and a badge when a panel closes.
 * It exists so the fiftieth field feels like the tenth, not to turn a profile
 * into a slot machine.
 *
 * `mode` is the only difference between joining and editing later: edit mode
 * lets you jump between panels, and finishing returns to the studio instead of
 * the one-time welcome.
 */
export function Induction({ identity, profile: initial, mode }: InductionProps) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [profile, setProfile] = useState(initial);
  const [answers, setAnswers] = useState<Answers>(initial.answers);
  const [stepIndex, setStepIndex] = useState(() => openingStep({ profile: initial, mode }));
  const [phase, setPhase] = useState<Phase>(initial.userType ? 'steps' : 'role');
  const [unlock, setUnlock] = useState<Badge | null>(null);
  // Seeded from what is already earned, so resuming or editing does not replay
  // badges the person has seen.
  const [announced, setAnnounced] = useState<Set<string>>(
    () => new Set(initial.userType ? badgesFor(initial.userType, initial.answers).map(badge => badge.id) : [])
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userType = profile.userType;

  const persist = async (patch: Parameters<typeof saveOnboarding>[1]) => {
    setBusy(true);
    setError(null);
    try {
      setProfile(await saveOnboarding(profile, patch));
      // rootLoader has already run and will not re-run on its own: navigating
      // from /profile to / keeps the root route matched, so its data would stay
      // as it was read on arrival. Without this you save a showreel and land on
      // a dashboard still asking you for a showreel. On /onboarding the root
      // route is not matched at all, so this costs nothing there.
      revalidator.revalidate();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  /* ── Door one: who are you here as ─────────────────────────────────────── */

  const pickRole = async (picked: UserType) => {
    // The name is already known from the Google account; asking for it again as
    // the very first question is the fastest way to make this feel like a form.
    const seeded: Answers = { full_name: identity.name, ...answers };
    setAnswers(seeded);
    const ok = await persist({ userType: picked, answers: seeded, stepIndex: 0 });
    if (!ok) return;
    setStepIndex(0);
    setPhase('steps');
  };

  if (!userType || phase === 'role') {
    return (
      <main className="shell">
        <div className="card card-lg">
          <StepBar left="Induction · 01" right="Takes about two minutes" />
          <h1 className="d">
            Pick your
            <br />
            way in.
          </h1>
          <p className="sub">
            Four doors, one studio. This decides what opens first, what you are matched with, and what the
            rest of this takes — you are not locked into it.
          </p>

          <div className="roles">
            {USER_TYPES.map(id => (
              <RoleCard
                key={id}
                label={ROLES[id].label}
                purpose={ROLES[id].purpose}
                promise={ROLES[id].promise}
                selected={false}
                onSelect={() => void pickRole(id)}
              />
            ))}
          </div>

          {error ? (
            <p className="err" style={{ marginTop: 20 }}>
              {error}
            </p>
          ) : null}

          <div className="rule" />
          <p className="ecosystem">
            <b>Learn</b> <i>→</i> <b>Create</b> <i>→</i> <b>Hire</b>
          </p>
        </div>
      </main>
    );
  }

  /* ── The panels ────────────────────────────────────────────────────────── */

  const steps = stepsFor(userType);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const role = ROLES[userType];

  const percent = completion(userType, answers);
  const rank = rankOf(userType, percent);
  const stepsDone = steps.filter(one => stepComplete(one, answers)).length;
  const missing = step.fields.filter(field => !field.optional && !isAnswered(field, answers));

  const setAnswer = (key: string, value: string | string[]) =>
    setAnswers(current => ({ ...current, [key]: value }));

  const advance = async (to: number, skipped: boolean) => {
    const ok = await persist({ answers, stepIndex: to, complete: to >= steps.length });
    if (!ok) return;

    // The badge lands on Continue rather than on the last keystroke — a panel
    // finishes while someone is still typing in it, and a toast then reads as an
    // interruption instead of a reward. Announced once, tracked by id.
    if (!skipped && stepComplete(step, answers) && !announced.has(step.badge.id)) {
      setUnlock(step.badge);
      setAnnounced(current => new Set(current).add(step.badge.id));
    }

    if (to >= steps.length) {
      navigate(mode === 'edit' ? '/' : '/welcome');
      return;
    }
    setStepIndex(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const last = stepIndex === steps.length - 1;

  return (
    <main className="shell">
      <div className="card wide">
        <StepBar
          left={mode === 'edit' ? 'Your profile' : `Induction · 0${stepIndex + 2}`}
          right={`${role.label} · ${step.kicker}`}
        />

        <ProgressRail
          rank={rank.label}
          nextRank={rank.next}
          toNext={rank.toNext}
          xp={earnedXp(userType, answers)}
          maxXp={maxXp(userType)}
          percent={percent}
          steps={steps.length}
          stepsDone={stepsDone}
        />

        {mode === 'edit' ? (
          <div className="chips" style={{ marginBottom: 28 }}>
            {steps.map((one, index) => (
              <button
                key={one.id}
                type="button"
                className="chip"
                aria-pressed={index === stepIndex}
                onClick={() => setStepIndex(index)}
              >
                {one.kicker}
              </button>
            ))}
          </div>
        ) : null}

        <h1 className="d">{step.title}</h1>
        <p className="sub">{step.blurb}</p>

        <div className="fields">
          {step.fields.map(field => (
            <FieldRow
              key={field.key}
              field={field}
              value={answers[field.key]}
              answered={isAnswered(field, answers)}
              onChange={value => setAnswer(field.key, value)}
            />
          ))}
        </div>

        {error ? (
          <p className="err" style={{ marginTop: 22 }}>
            {error}
          </p>
        ) : null}

        <div className="rule" />

        <div className="actions">
          <button className="btn" type="button" disabled={busy || missing.length > 0} onClick={() => void advance(stepIndex + 1, false)}>
            {busy
              ? 'Saving…'
              : mode === 'edit'
                ? last
                  ? 'Save and close'
                  : 'Save and continue →'
                : last
                  ? 'Finish induction →'
                  : 'Continue →'}
          </button>

          {stepIndex > 0 ? (
            <button className="btn ghost" type="button" disabled={busy} onClick={() => setStepIndex(stepIndex - 1)}>
              Back
            </button>
          ) : null}

          {/* Editing should never require walking to the end to keep a change. */}
          {mode === 'edit' && !last ? (
            <button className="btn ghost" type="button" disabled={busy} onClick={() => void advance(steps.length, true)}>
              Save and close
            </button>
          ) : null}

          {mode === 'induct' && stepIndex > 0 && !last ? (
            <button className="btn ghost" type="button" disabled={busy} onClick={() => void advance(stepIndex + 1, true)}>
              Skip for now
            </button>
          ) : null}
        </div>

        <p className="mono" style={{ marginTop: 16 }}>
          {missing.length > 0
            ? `${missing.length} answer${missing.length === 1 ? '' : 's'} left on this panel`
            : `Panel complete · unlocks ${step.badge.label}`}
        </p>
      </div>

      {unlock ? <UnlockToast label={unlock.label} note={unlock.note} onDone={() => setUnlock(null)} /> : null}
    </main>
  );
}

/**
 * Where to open.
 *
 * Induction resumes where it stopped, clamped — a finished profile stores a step
 * index one past the end, and `/profile` shares this component. Editing opens on
 * the first unfinished panel instead, so "add the missing thing" lands on the
 * missing thing rather than wherever the person happened to stop months ago.
 */
function openingStep({ profile, mode }: { profile: OnboardingProfile; mode: Mode }): number {
  if (!profile.userType) return 0;
  const steps = stepsFor(profile.userType);
  if (mode === 'edit') {
    const unfinished = steps.findIndex(step => !stepComplete(step, profile.answers));
    return unfinished === -1 ? 0 : unfinished;
  }
  return Math.min(Math.max(0, profile.stepIndex), steps.length - 1);
}

/** The route. Editing later goes through `pages/profile.tsx` instead. */
export default function Onboarding() {
  const { identity, profile } = useLoaderData() as OnboardingLoaderData;
  return <Induction identity={identity} profile={profile} mode="induct" />;
}

